import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
    House,
    ChartNoAxesCombined,
    Search,
    MessageSquareText,
    ShoppingBag,
    FileText,
    FolderKanban,
    ShoppingCart,
    Package,
    ListTodo,
    ScanSearch,
    Truck,
    LayoutGrid,
    Users,
    FileQuestion,
    Settings,
    ChevronDown,
    ChevronUp,
    LogOut,
    Menu,
    Bell,
    CheckCheck,
    Trash2,
    Wrench,
    CalendarDays,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import mark from '../../../public/pmp-favicon.png';
import {
    getNotifications,
    getUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    subscribeToNotifications,
} from '../../lib/notification-service';
import type { Notification } from '../../lib/types';
import type { StaffPermission } from '../../lib/access-control';
import type { LucideIcon } from 'lucide-react';

// ─── Nav Items ────────────────────────────────────────────────────────────────

interface NavItem {
    label: string;
    icon: LucideIcon;
    href: string;
    permission: StaffPermission;
    adminOnly?: boolean;
}

const MAIN_NAV: NavItem[] = [
    { label: 'Home', icon: House, href: '/', permission: 'dashboard' },
    { label: 'Analytics', icon: ChartNoAxesCombined, href: '/admin/analytics', permission: 'dashboard', adminOnly: true },
    { label: 'Messages', icon: MessageSquareText, href: '/admin/messages', permission: 'production' },
    { label: 'Orders', icon: ShoppingBag, href: '/admin/orders', permission: 'production' },
    { label: 'Quotes', icon: FileText, href: '/admin/quotes', permission: 'rfqs' },
    { label: 'Projects', icon: FolderKanban, href: '/admin/projects', permission: 'dashboard', adminOnly: true },
    { label: 'Carts', icon: ShoppingCart, href: '/admin/drafts', permission: 'drafts' },
    { label: 'Customers', icon: Users, href: '/admin/customers', permission: 'customers' },
];

const ORDER_VIEWS = [
    { label: 'Board', href: '/admin/orders/board', icon: LayoutGrid },
    { label: 'List', href: '/admin/orders/list', icon: ListTodo },
    { label: 'Calendar', href: '/admin/orders/calendar', icon: CalendarDays },
] as const;

const PRODUCTION_NAV: NavItem[] = [
    { label: 'Parts', icon: Package, href: '/production/parts', permission: 'production' },
    { label: 'Queue', icon: ListTodo, href: '/production', permission: 'production' },
    { label: 'Inspection', icon: ScanSearch, href: '/production/inspection', permission: 'production' },
    { label: 'Shipping', icon: Truck, href: '/production/shipping', permission: 'production' },
];

const TOOL_NAV: NavItem[] = [
    { label: 'Schedule', icon: CalendarDays, href: '/production/schedule', permission: 'schedule' },
    { label: 'Overview', icon: LayoutGrid, href: '/production/overview', permission: 'overview' },
    { label: 'Maintenance', icon: Wrench, href: '/production/maintenance', permission: 'maintenance' },
    { label: 'RFQs', icon: FileQuestion, href: '/admin/requests', permission: 'rfqs' },
    { label: 'Configuration', icon: Settings, href: '/admin', permission: 'dashboard', adminOnly: true },
];

function isActive(href: string, pathname: string): boolean {
    if (href === '/') return pathname === '/';
    if (href === '/admin') return pathname === '/admin';
    if (href === '/production') return pathname === '/production';
    if (href === '/admin/orders') return pathname.startsWith('/production/orders') || pathname === href || pathname.startsWith('/admin/orders/');
    return pathname === href || pathname.startsWith(href + '/');
}

// ─── Notification helpers ─────────────────────────────────────────────────────

function timeAgo(iso: string): string {
    const diff = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
}

function notifIcon(type: string) {
    switch (type) {
        case 'order_placed':    return '🛒';
        case 'order_shipped':   return '🚚';
        case 'order_completed': return '✅';
        case 'payment':        return '💳';
        case 'review':         return '🔍';
        default:               return '🔔';
    }
}

// ─── Notification Bell ────────────────────────────────────────────────────────

function NotificationBell({ userId }: { userId: string }) {
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const panelRef = useRef<HTMLDivElement>(null);

    const load = useCallback(async () => {
        const [notifs, count] = await Promise.all([
            getNotifications(userId),
            getUnreadCount(userId),
        ]);
        setNotifications(notifs);
        setUnreadCount(count);
        setLoading(false);
    }, [userId]);

    useEffect(() => {
        load();
        const unsub = subscribeToNotifications(userId, (n) => {
            setNotifications(prev => [n, ...prev]);
            setUnreadCount(c => c + 1);
        });
        return unsub;
    }, [userId, load]);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const handleOpen = () => {
        setOpen(o => !o);
    };

    const handleRead = async (id: string) => {
        await markAsRead(id);
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
        setUnreadCount(c => Math.max(0, c - 1));
    };

    const handleReadAll = async () => {
        await markAllAsRead(userId);
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        setUnreadCount(0);
    };

    const handleDelete = async (id: string, wasUnread: boolean) => {
        await deleteNotification(id);
        setNotifications(prev => prev.filter(n => n.id !== id));
        if (wasUnread) setUnreadCount(c => Math.max(0, c - 1));
    };

    return (
        <div className="relative" ref={panelRef}>
            <button
                onClick={handleOpen}
                className="relative p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all"
                title="Notifications"
            >
                <Bell size={18} />
                {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center h-4 min-w-[1rem] px-1 bg-rose-500 text-white text-[9px] font-black rounded-full animate-pulse">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {open && (
                <div className="absolute bottom-full right-0 mb-2 w-80 max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden flex flex-col max-h-[480px]">
                    {/* Panel header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 shrink-0">
                        <div className="flex items-center gap-2">
                            <Bell size={14} className="text-slate-500" />
                            <span className="text-sm font-bold text-slate-900">Notifications</span>
                            {unreadCount > 0 && (
                                <span className="text-[10px] font-black bg-rose-500 text-white px-1.5 py-0.5 rounded-full">{unreadCount}</span>
                            )}
                        </div>
                        {unreadCount > 0 && (
                            <button
                                onClick={handleReadAll}
                                className="flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-900 font-semibold transition-colors"
                            >
                                <CheckCheck size={12} />
                                Mark all read
                            </button>
                        )}
                    </div>

                    {/* Notification list */}
                    <div className="overflow-y-auto flex-1">
                        {loading ? (
                            <div className="py-8 text-center text-slate-500 text-sm">Loading…</div>
                        ) : notifications.length === 0 ? (
                            <div className="py-10 text-center">
                                <Bell size={28} className="mx-auto text-slate-300 mb-2" />
                                <p className="text-sm text-slate-500">No notifications yet</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-slate-100">
                                {notifications.map(n => (
                                    <div
                                        key={n.id}
                                        className={cn(
                                            'flex items-start gap-3 px-4 py-3 group transition-colors hover:bg-slate-50 cursor-pointer',
                                            !n.read && 'bg-blue-50/50'
                                        )}
                                        onClick={() => { if (!n.read) handleRead(n.id); }}
                                    >
                                        {/* Icon */}
                                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-base shrink-0 mt-0.5">
                                            {notifIcon(n.type)}
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <p className={cn('text-xs leading-snug', n.read ? 'text-slate-500' : 'text-slate-900 font-medium')}>
                                                {n.message}
                                            </p>
                                            <p className="text-[10px] text-slate-500 mt-0.5">{timeAgo(n.created_at)}</p>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                            {!n.read && (
                                                <button
                                                    onClick={e => { e.stopPropagation(); handleRead(n.id); }}
                                                    className="p-1 text-indigo-400 hover:text-indigo-300 rounded"
                                                    title="Mark read"
                                                >
                                                    <CheckCheck size={12} />
                                                </button>
                                            )}
                                            <button
                                                onClick={e => { e.stopPropagation(); handleDelete(n.id, !n.read); }}
                                                className="p-1 text-slate-500 hover:text-red-400 rounded"
                                                title="Delete"
                                            >
                                                <Trash2 size={12} />
                                            </button>
                                        </div>

                                        {/* Unread dot */}
                                        {!n.read && (
                                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0 mt-2" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {notifications.length > 0 && (
                        <div className="px-4 py-2.5 border-t border-slate-100 shrink-0">
                            <p className="text-[10px] text-slate-600 text-center">{notifications.length} notification{notifications.length !== 1 ? 's' : ''} total</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Admin Layout ─────────────────────────────────────────────────────────────

interface AdminLayoutProps {
    children: React.ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, userRole, staffRole, hasPermission, signOut } = useAuth();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [accountMenuOpen, setAccountMenuOpen] = useState(false);
    const [ordersOpen, setOrdersOpen] = useState(location.pathname.startsWith('/admin/orders') || location.pathname.startsWith('/production/orders'));
    const [search, setSearch] = useState('');
    const searchRef = useRef<HTMLInputElement>(null);

    const canSee = (item: NavItem) => (!item.adminOnly || userRole === 'admin') && hasPermission(item.permission);
    const matches = (item: NavItem) => item.label.toLowerCase().includes(search.trim().toLowerCase()) ||
        (item.href === '/admin/orders' && ORDER_VIEWS.some(view => view.label.toLowerCase().includes(search.trim().toLowerCase())));
    const mainItems = MAIN_NAV.filter(item => canSee(item) && matches(item));
    const productionItems = PRODUCTION_NAV.filter(item => canSee(item) && matches(item));
    const toolItems = TOOL_NAV.filter(item => canSee(item) && matches(item));
    const matchingOrderView = search.trim() && ORDER_VIEWS.find(view => view.label.toLowerCase().includes(search.trim().toLowerCase()));
    const firstResult = matchingOrderView && mainItems.some(item => item.href === '/admin/orders')
        ? matchingOrderView : [...mainItems, ...productionItems, ...toolItems][0];
    const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Staff';
    const roleLabel = staffRole?.name || (userRole === 'admin' ? 'Admin' : 'Employee');

    useEffect(() => {
        setMobileOpen(false);
        setAccountMenuOpen(false);
        setSearch('');
        if (location.pathname.startsWith('/admin/orders') || location.pathname.startsWith('/production/orders')) setOrdersOpen(true);
    }, [location.pathname]);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setMobileOpen(false);
                setAccountMenuOpen(false);
            }
            if (event.key === '/' && !(event.target instanceof HTMLInputElement) && !(event.target instanceof HTMLTextAreaElement)) {
                event.preventDefault();
                searchRef.current?.focus();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        if (!accountMenuOpen) return;
        const onPointerDown = (event: PointerEvent) => {
            if (!(event.target instanceof Element) || !event.target.closest('[data-account-menu]')) {
                setAccountMenuOpen(false);
            }
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [accountMenuOpen]);

    const goTo = (href: string) => {
        navigate(href);
        setMobileOpen(false);
        setAccountMenuOpen(false);
        setSearch('');
    };

    const navSection = (items: NavItem[], heading?: string) => items.length > 0 && (
        <div className="space-y-0.5">
            {heading && <h2 className="px-3 pb-2 pt-5 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{heading}</h2>}
            {items.map(item => {
                const active = isActive(item.href, location.pathname);
                if (item.href === '/admin/orders') {
                    return (
                        <div key={item.href}>
                            <button type="button" onClick={() => setOrdersOpen(open => !open)} aria-expanded={ordersOpen || Boolean(search)}
                                className={cn('flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[13px] font-medium transition-colors',
                                    active ? 'bg-slate-100 text-slate-950' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950')}>
                                <item.icon size={17} strokeWidth={active ? 2.1 : 1.8} className={active ? 'text-slate-900' : 'text-slate-500'} />
                                <span className="flex-1">Orders</span>
                                <ChevronDown size={15} className={cn('text-slate-400 transition-transform', (ordersOpen || search) && 'rotate-180')} />
                            </button>
                            {(ordersOpen || Boolean(search)) && (
                                <div className="ml-5 mt-1 space-y-0.5 border-l border-slate-200 pl-3">
                                    {ORDER_VIEWS.filter(view => !search.trim() || item.label.toLowerCase().includes(search.trim().toLowerCase()) || view.label.toLowerCase().includes(search.trim().toLowerCase())).map(view => (
                                        <button key={view.href} type="button" onClick={() => goTo(view.href)}
                                            aria-current={location.pathname === view.href ? 'page' : undefined}
                                            className={cn('flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-xs font-medium transition-colors',
                                                location.pathname === view.href ? 'bg-slate-100 text-slate-950' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900')}>
                                            <view.icon size={14} /> {view.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                }
                return (
                    <button key={item.href} type="button" onClick={() => goTo(item.href)}
                        aria-current={active ? 'page' : undefined}
                        className={cn('flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[13px] font-medium transition-colors',
                            active ? 'bg-slate-100 text-slate-950' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950')}>
                        <item.icon size={17} strokeWidth={active ? 2.1 : 1.8} className={active ? 'text-slate-900' : 'text-slate-500'} />
                        <span>{item.label}</span>
                    </button>
                );
            })}
        </div>
    );

    const sidebar = (
        <div className="flex h-full flex-col bg-white">
            <div className="px-5 pb-5 pt-6">
                <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 rounded-lg text-slate-950">
                    <img src={mark} alt="" className="h-9 w-9 object-contain" />
                    <span className="flex flex-col leading-tight">
                        <span className="text-[15px] font-bold tracking-tight">PrintMyParts</span>
                        <span className="text-[11px] text-slate-500">Workspace</span>
                    </span>
                </Link>
            </div>
            <div className="px-3 pb-4">
                <div className="relative">
                    <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input ref={searchRef} value={search} onChange={event => setSearch(event.target.value)}
                        onKeyDown={event => { if (event.key === 'Enter' && firstResult) goTo(firstResult.href); }}
                        type="search" aria-label="Search pages" placeholder="Search pages"
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:border-slate-400 focus:bg-white" />
                </div>
                <p className="mt-1.5 px-1 text-[10px] text-slate-400">Press / to search pages</p>
            </div>
            <nav aria-label="Admin navigation" className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 pb-6">
                {navSection(mainItems)}
                {navSection(productionItems, 'Production')}
                {search && !firstResult && <p className="px-3 py-4 text-sm text-slate-400">No pages found</p>}
                {toolItems.length > 0 && (
                    <details className="pt-4" open={Boolean(search)}>
                        <summary className="cursor-pointer rounded-lg px-3 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-50">More tools</summary>
                        <div className="pt-1">{navSection(toolItems)}</div>
                    </details>
                )}
            </nav>
            <div data-account-menu className="relative border-t border-slate-200 p-3">
                {accountMenuOpen && (
                    <div role="menu" aria-label="Account menu" className="absolute bottom-full left-3 right-3 mb-2 overflow-hidden rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl">
                        <div className="border-b border-slate-100 px-3 py-2.5">
                            <p className="truncate text-xs font-semibold text-slate-900">{displayName}</p>
                            <p className="truncate text-[11px] text-slate-500">{user?.email}</p>
                            <p className="mt-1 text-[10px] text-slate-400">{roleLabel}</p>
                        </div>
                        <button role="menuitem" type="button" onClick={() => goTo('/settings')}
                            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50">
                            <Settings size={16} /> Settings
                        </button>
                        <button role="menuitem" type="button" onClick={() => { setAccountMenuOpen(false); void signOut(); }}
                            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50">
                            <LogOut size={16} /> Sign out
                        </button>
                    </div>
                )}
                <div className="flex items-center gap-1">
                    <button type="button" onClick={() => setAccountMenuOpen(open => !open)}
                        aria-haspopup="menu" aria-expanded={accountMenuOpen} aria-label={`Account menu for ${displayName}`}
                        className={cn('flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2 py-2 text-left hover:bg-slate-50', accountMenuOpen && 'bg-slate-50')}>
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
                            {displayName.charAt(0).toUpperCase()}
                        </span>
                        <span className="min-w-0 flex-1 leading-tight">
                            <span className="block truncate text-xs font-semibold text-slate-900">{displayName}</span>
                            <span className="block truncate text-[10px] text-slate-500">ID: {user?.id || '—'}</span>
                        </span>
                        <ChevronUp size={14} className={cn('shrink-0 text-slate-400 transition-transform', accountMenuOpen && 'rotate-180')} />
                    </button>
                    {user && <NotificationBell userId={user.id} />}
                </div>
            </div>
        </div>
    );

    return (
        <div className="flex min-h-[100dvh] bg-white text-slate-900">
            <aside className="sticky top-0 hidden h-[100dvh] w-[260px] shrink-0 border-r border-slate-200 lg:block">
                {sidebar}
            </aside>
            {mobileOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <button type="button" aria-label="Close navigation" onClick={() => setMobileOpen(false)} className="absolute inset-0 bg-slate-900/30" />
                    <aside className="relative h-full w-[min(85vw,280px)] border-r border-slate-200 shadow-xl">{sidebar}</aside>
                </div>
            )}
            <div className="min-w-0 flex-1">
                <header className="flex h-14 items-center justify-between border-b border-slate-200 px-4 lg:hidden">
                    <button type="button" aria-label="Open navigation" onClick={() => setMobileOpen(true)} className="rounded-lg p-2 text-slate-600 hover:bg-slate-100"><Menu size={20} /></button>
                    <span className="text-sm font-semibold">PrintMyParts</span>
                    <span className="w-9" />
                </header>
                <main className="mx-auto w-full max-w-[1700px] p-4 sm:p-6 lg:p-8" key={location.pathname}>
                    {children}
                </main>
            </div>
        </div>
    );
}
